import type { CalendarEvent } from '../../lib/google/events';
import { clsx } from '../../components/clsx';
import { formatEventTime } from '../../lib/date';

/**
 * One event: [time, w-14 tabular-nums][3px calendar-colour bar][title truncate].
 *
 * No location, no attendees, no description — at ~350px wide any of them
 * would push the title out, and the spec asks for title + time + colour.
 *
 * The colour bar is an inline style because it is a RUNTIME hex from Google's
 * calendarList `backgroundColor`. That is not a hardcoded colour (the lint
 * rule bans hex *literals* in source); the fallback, which is a literal, is a
 * Tailwind token class.
 */
export function EventRow({ event }: { event: CalendarEvent }) {
  const now = Date.now();
  const past = !event.allDay && event.end < now;
  const live = !event.allDay && event.start <= now && event.end > now;

  return (
    <div
      className="flex items-center gap-1.5 py-[2px]"
      title={`${event.allDay ? 'All day' : formatEventTime(new Date(event.start))} · ${event.title}`}
    >
      <span
        className={clsx(
          'w-14 shrink-0 text-[11px] leading-tight tabular-nums',
          live ? 'font-semibold text-ink' : 'text-ink-subtle',
        )}
      >
        {event.allDay ? 'all day' : formatEventTime(new Date(event.start))}
      </span>
      <span
        className={clsx('h-3.5 w-[3px] shrink-0 rounded-full', event.color === null && 'bg-accent-cal')}
        style={event.color === null ? undefined : { backgroundColor: event.color }}
        aria-hidden
      />
      <span
        className={clsx(
          'truncate text-[12px] leading-tight',
          past ? 'text-ink-subtle line-through' : live ? 'font-semibold text-ink' : 'text-ink',
        )}
      >
        {event.title}
      </span>
    </div>
  );
}
